/**
 * useRefEditWorkflow — Track 3 Phase 5
 *
 * Owns the in-place external-reference-edit (REFEDIT) workflow surface
 * fragments that previously lived inline in `App.tsx`. Phase FIN.1
 * shipped two cooperating pieces:
 *
 *   1. A status-bar mini-badge that surfaces the referenced block
 *      name whenever the active drawing space is a `refedit` space.
 *   2. The in-canvas `<RefeditToolbar>` toolbar (REFSET / REFCLOSE
 *      add / remove / save / discard) — already a self-contained
 *      component in `components/RefeditToolbar.tsx` that subscribes
 *      to `useEditorStore` directly.
 *
 * Track 3 Phase 5 extracts the App.tsx-level glue (active-space
 * derivation + indicator JSX) into this hook. The toolbar itself
 * stays in its component file because it has no App.tsx-level wiring.
 *
 * Returned shape:
 *   {
 *     isActive,           // active space is `refedit`
 *     blockName,          // referenced block name (or "" if none)
 *     statusIndicator,    // <span> mini-badge (or null when inactive)
 *   }
 *
 * The orchestrator drops `statusIndicator` into the status-bar
 * fragment that previously contained the inline IIFE. `isActive` /
 * `blockName` are exposed for external callers (e.g. a future
 * keyboard-shortcut handler that toggles a panel) but are currently
 * unused by App.tsx.
 *
 * AutoCAD parity:
 *   AutoCAD displays the REFEDIT toolbar AND a status-line indicator
 *   while an in-place reference edit is active. We mirror both — the
 *   toolbar inside the canvas, the badge in the quickbar.
 */

import type { ReactElement } from "react";

import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

export interface UseRefEditWorkflowResult {
  /** Whether an in-place REFEDIT session is currently active. */
  isActive: boolean;
  /** Referenced block name while active, empty string otherwise. */
  blockName: string;
  /**
   * The status-bar mini-badge rendered in the quickbar fragment. Returns
   * `null` while no refedit session is active so the orchestrator can
   * inline it without extra guards.
   */
  statusIndicator: ReactElement | null;
}

export function useRefEditWorkflow(): UseRefEditWorkflowResult {
  const t = useT();
  // Subscribe directly to the active geometry-document space so the
  // indicator re-renders the moment the user enters or leaves a
  // refedit session.
  const activeGeoSpace = useEditorStore((store) =>
    store.geometryDocument.spaces.find(
      (s) => s.id === store.geometryDocument.activeSpaceId,
    ),
  );

  if (activeGeoSpace?.kind !== "refedit") {
    return {
      isActive: false,
      blockName: "",
      statusIndicator: null,
    };
  }

  const blockName = activeGeoSpace.refeditBlockName ?? "";
  const statusIndicator = (
    <span
      className="mini-badge drawing-stage-refedit-tint"
      data-testid="status-refedit-indicator"
      title={t("status.refedit.editing", { name: blockName })}
    >
      {t("status.refedit.editing", { name: blockName })}
    </span>
  );

  return {
    isActive: true,
    blockName,
    statusIndicator,
  };
}
